import { useState, useCallback, useRef } from 'react';
import { voicemailService, VoicemailRecord, VoicemailFilters } from '@/services/features/VoicemailService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export const useVoicemails = () => {
  const [data, setData] = useState<VoicemailRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { toast } = useToast();
  const { user, token } = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async (filters: VoicemailFilters) => {
    if (!user?.id) {
      console.warn('User ID not available for fetching voicemails');
      return;
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const response = await voicemailService.fetchUnreadVoicemails(
        user.id.toString(),
        filters,
        token
      );
      
      // Only update state if request wasn't aborted
      if (!abortControllerRef.current.signal.aborted) {
        // Ensure we always have an array, even if API returns undefined/null
        setData(response?.callHistory || []);
        setTotalElements(response?.totalItems || 0);
        setTotalPages(response?.totalPages || 0);
      }
    } catch (error: any) {
      // Don't handle error if request was aborted
      if (error.name !== 'AbortError' && !abortControllerRef.current?.signal.aborted) {
        console.error('Error fetching voicemails:', error);
        setData([]);
        setTotalElements(0);
        setTotalPages(0);
        
        // Show error toast
        setTimeout(() => {
          toast({
            title: "Error",
            description: "Failed to fetch call history records",
            variant: "destructive",
            duration: 1000
          });
        }, 0);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [user?.id, token, toast]);

  return {
    data,
    loading,
    totalElements,
    totalPages,
    fetchData,
  };
};
